import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ymd } from '@/components/ui/PeriodPicker';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { GST_STATE_MAP } from '@/lib/validation';
import { NOTE_REASONS, alterNote, createNote, getNoteDocs, type NoteInput, type NoteKind } from '@/api/accountsBooks';
import { ChipTag, DateField, F, FormFooter, FormTitle, Msgs, Posting, ReadBox, Sec } from './voucherFormParts';
import { curBal, dd, inr, r2 } from './voucherFormat';
import { Grid } from './tradeParts';
import type { FormProps } from './useVoucherForm';

/* Credit Note (goods returned) · Rate Difference · Debit Note — laid out like Tally's Credit Note
   (Alt+F6) / Debit Note (Alt+F5), as the prototype: note no. and original invoice / bill →
   Party A/c → returns ledger → items with the tax lines under them → Bill-wise Details (Agst Ref
   the original document, excess On Account) → Narration → Posting. */

const TITLE: Record<NoteKind, string> = { cn: 'Credit Note', rd: 'Credit Note', dn: 'Debit Note' };
const SAVE: Record<NoteKind, string> = { cn: 'Save Credit Note', rd: 'Save Rate Difference Note', dn: 'Save Debit Note' };
const HEAD: Record<NoteKind, string> = { cn: 'Sales Returns', rd: 'Rate Difference Allowed', dn: 'Purchase Returns' };
const r3 = (x: number) => Math.round(x * 1000) / 1000;
const split = (taxable: number, g: number, intra: boolean) => {
  const t = r2((taxable * g) / 100), c = intra ? r2(t / 2) : 0;
  return { cgst: c, sgst: intra ? r2(t - c) : 0, igst: intra ? 0 : t };
};

interface State { date: string; party: number | ''; doc: number | ''; reason: string; narr: string; qty: Record<number, number>; diff: Record<number, number> }

export function NoteForm({ voucher, ledgers, nextNo, onClose, kind: newKind }: FormProps & { kind: NoteKind }) {
  const qc = useQueryClient();
  const toast = useToast();
  const d = voucher?.details;
  const kind = (d?.kind ?? newKind) as NoteKind;
  const credit = kind !== 'dn';
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const parties = ledgers.filter((l) => l.role === (credit ? 'D' : 'C'));
  const today = ymd(new Date());

  const [s, setS] = useState<State>(() => (voucher && d
    ? {
      date: voucher.date, party: d.partyLedgerId, doc: d.againstId ?? '', reason: d.reason ?? NOTE_REASONS[kind][0], narr: voucher.narration ?? '',
      qty: Object.fromEntries((d.lines ?? []).map((l) => [l.srcLine, l.qty])), diff: Object.fromEntries((d.lines ?? []).map((l) => [l.srcLine, l.diff ?? 0])),
    }
    : { date: today, party: '', doc: '', reason: NOTE_REASONS[kind][0], narr: '', qty: {}, diff: {} }));
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));

  // The party's invoices / bills with what is still open on each line (without this note, when altering).
  const docsQ = useQuery({
    queryKey: ['acc-books', 'note-docs', kind, s.party, voucher?.id ?? 0],
    queryFn: () => getNoteDocs(kind, s.party as number, voucher?.id),
    enabled: s.party !== '',
  });
  const docs = docsQ.data ?? [];
  // A new party: start on its latest invoice / bill, as the prototype does.
  useEffect(() => {
    if (!voucher && docsQ.data && s.doc === '' && docsQ.data[0]) set({ doc: docsQ.data[0].id });
  }, [docsQ.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const doc = docs.find((x) => x.id === s.doc);
  const party = s.party === '' ? undefined : byId.get(s.party);

  // Prototype rate-difference lines start with the invoiced quantity.
  const qtyOf = (srcLine: number, invoiced: number) => s.qty[srcLine] ?? (kind === 'rd' ? invoiced : 0);
  const lines = (doc?.lines ?? []).map((l) => {
    const qty = qtyOf(l.srcLine, l.qty);
    const diff = s.diff[l.srcLine] ?? 0;
    const per = kind === 'rd' ? diff : l.rate;
    const taxable = r2(qty * per);
    const gst = credit || doc?.registered ? l.gst : 0;
    return { ...l, qty0: l.qty, nqty: qty, diff, taxable, gstUsed: gst, ...split(taxable, gst, doc?.intra ?? true) };
  });
  const t = lines.reduce((a, l) => ({ taxable: r2(a.taxable + l.taxable), cgst: r2(a.cgst + l.cgst), sgst: r2(a.sgst + l.sgst), igst: r2(a.igst + l.igst) }), { taxable: 0, cgst: 0, sgst: 0, igst: 0 });
  const total = r2(t.taxable + t.cgst + t.sgst + t.igst);
  const pend = doc?.pending ?? 0;
  const agst = r2(Math.min(pend, total)), onAcc = r2(total - agst);

  const block: string[] = [];
  if (!s.date) block.push('Enter the date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!party) block.push(`Choose the ${credit ? 'customer' : 'supplier'} (Party A/c name).`);
  if (!doc) block.push(credit ? 'Pick the invoice this note is against.' : 'Pick the bill this note is against.');
  if (doc && s.date < doc.date) block.push(`A note can't be dated before its ${credit ? 'invoice' : 'bill'} (${dd(doc.date)}).`);
  if (!(t.taxable > 0)) block.push(kind === 'rd' ? 'Enter the rate reduction on at least one line.' : 'Enter the quantity returned on at least one line.');
  for (const l of lines) {
    if (l.nqty < 0 || l.diff < 0) block.push(`${l.name}: quantities and amounts can’t be negative.`);
    if (kind !== 'rd' && l.nqty > r3(l.qty0 - l.returned) + 0.0001) block.push(`${l.name}: only ${r3(l.qty0 - l.returned)} of ${l.qty0} ${kind === 'dn' ? 'billed' : 'invoiced'} can still be returned.`);
    if (kind === 'rd' && l.diff > r2(l.rate - l.reduced) + 0.004) block.push(`${l.name}: rate can be reduced by at most ${inr(r2(l.rate - l.reduced))} per unit.`);
    if (kind === 'rd' && l.nqty > l.qty0 + 0.0001) block.push(`${l.name}: quantity can't exceed the ${l.qty0} invoiced.`);
  }

  const sub = [...(agst > 0 ? [`Agst Ref ${doc?.billNo} · ${inr(agst)}`] : []), ...(onAcc > 0.004 ? [`On Account · ${inr(onAcc)}`] : [])];
  const txw = credit ? 'Output' : 'Input';
  const post = credit
    ? [{ l: HEAD[kind], a: t.taxable }, { l: 'Output CGST', a: t.cgst }, { l: 'Output SGST', a: t.sgst }, { l: 'Output IGST', a: t.igst }, { l: party?.name ?? '', a: -total, sub }]
    : [{ l: party?.name ?? '', a: total, sub }, { l: HEAD[kind], a: -t.taxable }, { l: 'Input CGST', a: -t.cgst }, { l: 'Input SGST', a: -t.sgst }, { l: 'Input IGST', a: -t.igst }];

  const build = (): NoteInput => ({
    kind, date: s.date, againstId: s.doc as number, reason: s.reason, narration: s.narr.trim(),
    lines: lines.filter((l) => l.taxable > 0).map((l) => ({ srcLine: l.srcLine, qty: l.nqty, ...(kind === 'rd' ? { diff: l.diff } : {}) })),
  });
  const save = useMutation({
    mutationFn: (b: NoteInput) => (voucher ? alterNote(voucher.id, b) : createNote(b)),
    onSuccess: (v) => { toast.push(`${TITLE[kind]} ${v.voucherNo} ${voucher ? 'altered' : 'saved'}`, 'success'); qc.invalidateQueries({ queryKey: ['acc-books'] }); onClose(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const num = (v: string, dp: number) => Math.round((Number(v) || 0) * 10 ** dp) / 10 ** dp;
  const stateName = party?.stateCode ? GST_STATE_MAP[party.stateCode] ?? party.stateCode : '';

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={<FormTitle title={TITLE[kind]} no={voucher?.voucherNo ?? nextNo}
        chip={<ChipTag tone={credit ? 'o' : 'b'}>{credit ? 'Reduces receivable' : 'Reduces payable'}</ChipTag>}
        extra={kind === 'rd' ? <span className="text-xs font-semibold text-[var(--text-secondary)]">· rate difference</span> : undefined} />}
      footer={
        <FormFooter left={<>Note total <b>{inr(total)}</b></>} version={voucher ? voucher.version + 1 : undefined}
          saveLabel={SAVE[kind]} blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />
      }
    >
      <Grid>
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v })} />
        <F label={`Original ${credit ? 'invoice' : 'bill'} no. *`} className="col-span-2">
          <Select value={s.doc} onChange={(e) => set({ doc: Number(e.target.value), qty: {}, diff: {} })} disabled={!docs.length}>
            {!docs.length && <option value="">{s.party === '' ? 'Choose the party first' : docsQ.isLoading ? 'Loading…' : `No ${credit ? 'invoices' : 'bills'}`}</option>}
            {docs.map((x) => <option key={x.id} value={x.id}>{x.billNo} · {dd(x.date)} · {inr(x.total)}</option>)}
          </Select>
        </F>
        <F label="Original date"><Input disabled value={doc ? dd(doc.date) : '—'} /></F>
        <F label="Party A/c name *" className="col-span-2">
          <SearchableSelect
            options={parties.map((p) => ({ value: p.id, label: p.name, sublabel: [p.partyCode, p.gstin ?? 'Unregistered'].filter(Boolean).join(' · ') }))}
            value={s.party} placeholder={credit ? 'Customer' : 'Supplier'}
            onChange={(v) => set({ party: v === '' ? '' : Number(v), doc: '', qty: {}, diff: {} })}
          />
        </F>
        <F label="Cur Bal"><ReadBox value={curBal(party)} /></F>
        <F label="GSTIN · State"><Input disabled className="font-mono" value={party ? `${party.gstin || 'Unregistered'} · ${stateName}` : ''} /></F>
        <F label={`${credit ? 'Sales' : 'Purchase'} ledger`} className="col-span-2"><Input disabled value={`${HEAD[kind]} · ${doc?.intra === false ? 'IGST' : 'CGST + SGST'}`} /></F>
        <F label="Reason for issuing note *" className="col-span-2">
          <Select value={s.reason} onChange={(e) => set({ reason: e.target.value })}>{NOTE_REASONS[kind].map((r) => <option key={r}>{r}</option>)}</Select>
        </F>
      </Grid>

      <Sec>Items</Sec>
      <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full min-w-[760px] whitespace-nowrap text-xs">
          <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
            {kind === 'rd'
              ? <tr><th className="px-2 py-1.5">Name of item</th><th className="px-2 py-1.5">HSN</th><th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Invoice rate</th><th className="px-2 py-1.5 text-right">Reduced by</th><th className="px-2 py-1.5">per</th><th className="px-2 py-1.5 text-right">GST</th><th className="px-2 py-1.5 text-right">Amount</th></tr>
              : <tr><th className="px-2 py-1.5">Name of item</th><th className="px-2 py-1.5">HSN</th><th className="px-2 py-1.5 text-right">{kind === 'dn' ? 'Billed' : 'Invoiced'}</th><th className="px-2 py-1.5 text-right">Qty returned</th><th className="px-2 py-1.5 text-right">Rate</th><th className="px-2 py-1.5">per</th><th className="px-2 py-1.5 text-right">GST</th><th className="px-2 py-1.5 text-right">Amount</th></tr>}
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.srcLine} className="border-t border-[var(--border-color)]">
                <td className="px-2 py-1.5">{l.name}</td>
                <td className="px-2 py-1.5 font-mono">{l.hsn}</td>
                {kind === 'rd' ? (
                  <>
                    <td className="px-2 py-1.5 text-right"><Input type="number" min={0} step="1" className="ml-auto !w-[80px] text-right font-mono" value={l.nqty || ''} placeholder="0" onChange={(e) => set({ qty: { ...s.qty, [l.srcLine]: num(e.target.value, 3) } })} /></td>
                    <td className="px-2 py-1.5 text-right">{inr(l.rate)}{l.reduced ? <span className="text-[var(--text-muted)]"> (−{inr(l.reduced)} earlier)</span> : null}</td>
                    <td className="px-2 py-1.5 text-right"><Input type="number" min={0} step="0.01" className="ml-auto !w-[90px] text-right font-mono" value={l.diff || ''} placeholder="0" onChange={(e) => set({ diff: { ...s.diff, [l.srcLine]: num(e.target.value, 2) } })} /></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-1.5 text-right">{l.qty0}{l.returned ? <span className="text-[var(--text-muted)]"> ({l.returned} returned)</span> : null}</td>
                    <td className="px-2 py-1.5 text-right"><Input type="number" min={0} step="1" className="ml-auto !w-[80px] text-right font-mono" value={l.nqty || ''} placeholder="0" onChange={(e) => set({ qty: { ...s.qty, [l.srcLine]: num(e.target.value, 3) } })} /></td>
                    <td className="px-2 py-1.5 text-right">{inr(l.rate)}</td>
                  </>
                )}
                <td className="px-2 py-1.5 text-[var(--text-muted)]">{l.unit.toLowerCase()}</td>
                <td className="px-2 py-1.5 text-right">{l.gstUsed}%</td>
                <td className="px-2 py-1.5 text-right font-mono">{inr(l.taxable)}</td>
              </tr>
            ))}
            {!lines.length && <tr><td colSpan={8} className="px-2 py-2 text-[var(--text-muted)]">{doc ? 'No lines.' : `Pick the ${credit ? 'invoice' : 'bill'} above.`}</td></tr>}
          </tbody>
          <tfoot>
            <tr className="border-t border-[var(--border-color)]"><td colSpan={7} className="px-2 py-1.5 text-right font-bold">Taxable value</td><td className="px-2 py-1.5 text-right font-mono font-bold">{inr(t.taxable)}</td></tr>
            {([[`${txw} CGST`, t.cgst], [`${txw} SGST`, t.sgst], [`${txw} IGST`, t.igst]] as [string, number][]).filter(([, a]) => a).map(([nm, a]) => (
              <tr key={nm}><td colSpan={7} className="px-2 py-1 text-right italic">{nm}</td><td className="px-2 py-1 text-right font-mono">{inr(a)}</td></tr>
            ))}
            <tr><td colSpan={7} className="px-2 py-1.5 text-right font-bold">Note total</td><td className="px-2 py-1.5 text-right font-mono font-bold">{inr(total)}</td></tr>
          </tfoot>
        </table>
      </div>

      <Sec>Bill-wise Details{party ? ` for ${party.name}` : ''}</Sec>
      <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full whitespace-nowrap text-xs">
          <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
            <tr><th className="px-2 py-1.5">Type of Ref</th><th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">{credit ? 'Invoice' : 'Bill'} pending</th><th className="px-2 py-1.5 text-right">Amount</th><th /></tr>
          </thead>
          <tbody>
            {agst > 0 && <tr className="border-t border-[var(--border-color)]"><td className="px-2 py-1.5">Agst Ref</td><td className="px-2 py-1.5 font-mono">{doc?.billNo}</td><td className="px-2 py-1.5">{inr(pend)}</td><td className="px-2 py-1.5 text-right font-mono">{inr(agst)}</td><td className="px-2 py-1.5 text-[var(--text-muted)]">{credit ? 'Cr' : 'Dr'}</td></tr>}
            {onAcc > 0.004 && <tr className="border-t border-[var(--border-color)]"><td className="px-2 py-1.5">On Account</td><td className="px-2 py-1.5">—</td><td className="px-2 py-1.5 text-[var(--text-muted)]">beyond the {credit ? 'invoice' : 'bill'} balance — stays with the party</td><td className="px-2 py-1.5 text-right font-mono">{inr(onAcc)}</td><td className="px-2 py-1.5 text-[var(--text-muted)]">{credit ? 'Cr' : 'Dr'}</td></tr>}
            {!(total > 0) && <tr><td colSpan={5} className="px-2 py-2 text-[var(--text-muted)]">Enter the items above.</td></tr>}
          </tbody>
        </table>
      </div>

      <Msgs block={block} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder={`${s.reason} – agst ${doc?.billNo ?? ''}`} onChange={(e) => set({ narr: e.target.value })} />
      </F>
      <Sec>Posting</Sec>
      <Posting lines={post} />
    </Modal>
  );
}
